import * as Yup from 'yup';
import {TFunction} from 'i18next';
import {LectureFormValues} from './types';
import {TSlot, TTimetableItem} from '../../../../redux/timetable/timetable.type';
import {combineDateTime} from '../../../../library/dates';
import {TConferenceDay} from '../../hooks/useTimetableDays';
import {clearText} from '../../../../component/textEditor/TextEditorHelpers';

/** Max plain-text length for the lecture/lecturer summary fields. */
export const SUMMARY_MAX = 1000;

/**
 * Yup rule for a rich-text summary field. Counts plain-text length (HTML stripped
 * via clearText) so it matches the character counter shown in the editor.
 */
export const summaryMaxRule = (max: number, message: string) =>
    Yup.string().test('summaryMaxLength', message, value => clearText(value || '').length <= max);

const findBreakAtTime = (
    dayLectures: TTimetableItem[],
    time: number,
    selfTtid?: number,
) => dayLectures.find(b =>
    b.isBreak
    && b.ttid !== selfTtid
    && b.time <= time
    && (b.timeEnd === undefined ? b.time === time : time < b.timeEnd),
);

export const resolveSlotTime = (
    tsid: number | null | undefined,
    dayIndex: number,
    slots: TSlot[],
    conferenceDays: TConferenceDay[],
): number | null => {
    if(!tsid) return null;
    const slot = slots.find(s => s.tsid === tsid);
    const day = conferenceDays[dayIndex];
    if(!slot || !day) return null;
    return combineDateTime(day.timestamp, slot.timeStart);
};

export type LectureTimeData = {
    slots: TSlot[];
    conferenceDays: TConferenceDay[];
    isWithin: (time: number) => boolean;
    lecturesByDay: Record<number, TTimetableItem[]>;
    ttid?: number;
};

/** Max length for all link fields. Change here to adjust every link limit at once. */
export const MAX_LINK_LENGTH = 255;

/**
 * The lecture `link` field is stored as a JSON string: {title, link}.
 * Older records may hold a plain URL string, so we fall back gracefully.
 */
export const parseLectureLink = (raw?: string | null): {linkName: string; link: string} => {
    if(!raw) return {linkName: '', link: ''};
    try {
        const parsed = JSON.parse(raw);
        if(parsed && typeof parsed === 'object')
            return {linkName: parsed.title ?? '', link: parsed.link ?? ''};
    } catch {
        // Looks like our {title, link} envelope but won't parse — a corrupted/truncated record.
        // Salvage the fields by hand rather than exposing the raw JSON string to the user.
        if(raw.trimStart().startsWith('{')) {
            return {
                linkName: raw.match(/"title"\s*:\s*"([^"]*)/)?.[1] ?? '',
                link: raw.match(/"link"\s*:\s*"([^"]*)/)?.[1] ?? '',
            };
        }
    }
    return {linkName: '', link: raw};
};

export const serializeLectureLink = (linkName: string, link: string): string | null => {
    const title = (linkName ?? '').trim();
    const url = (link ?? '').trim();
    if(!title && !url) return null;
    return JSON.stringify({title, link: url});
};

export const initialValueLecture = (lecture?: TTimetableItem | null): Omit<LectureFormValues, 'dayIndex'> => ({
    title: lecture?.title ?? '',
    lecturerIds: lecture?.lecturers?.map(l => l.lid) ?? [],
    tids: lecture?.tickets?.map(t => t.tid) ?? [],
    thid: lecture?.thid ?? null,
    tsid: lecture?.tsid ?? null,
    description: lecture?.description ?? '',
    amount: lecture ? lecture.amount ?? null : 0,
    price: lecture?.price ?? 0,
    hasCasualPrice: lecture?.priceCasual != null,
    priceCasual: lecture?.priceCasual ?? 0,
    isRequired: !!lecture?.isRequired,
    ...parseLectureLink(lecture?.link),
    active: lecture? !!lecture?.active : true,
});

export const buildValidationSchema = (
    t: TFunction,
    data: LectureTimeData,
) =>
    Yup.object({
        title: Yup.string().required(t('fieldRequired')),
        description: summaryMaxRule(SUMMARY_MAX, t('summaryTooLong', {max: SUMMARY_MAX})),
        tsid: Yup.number().nullable().required(t('fieldRequired'))
            .test('lectureTime', function(value) {
                const time = resolveSlotTime(value, this.parent.dayIndex, data.slots, data.conferenceDays);
                if(time == null) return true;
                if(!data.isWithin(time)) return this.createError({message: t('lectureOutsideEventHours')});
                const dayLectures = data.lecturesByDay[data.conferenceDays[this.parent.dayIndex].timestamp] ?? [];
                if(findBreakAtTime(dayLectures, time, data.ttid)) return this.createError({message: t('breakOverlapsTime')});
                return true;
            }),
        dayIndex: Yup.number().min(-1, t('fieldRequired')).required(t('fieldRequired')),
        // Unlimited capacity is stored as null (the field is disabled then); any entered
        // value must be 1+. `defined()` rejects undefined (a cleared field), while the
        // empty string and an explicit 0 are caught by typeError/min — so all three are blocked.
        amount: Yup.number().nullable().defined(t('fieldRequired')).min(1, t('fieldRequired')).typeError(t('fieldRequired')).integer(t('mustBeInteger')),
        price: Yup.number().min(0).integer(t('mustBeInteger')),
        priceCasual: Yup.number().min(0).integer(t('mustBeInteger')),
        link: Yup.string().url(t('linkInvalidUrl')).max(MAX_LINK_LENGTH, t('linkTooLong', {max: MAX_LINK_LENGTH})),
        linkName: Yup.string().max(20, t('linkNameTooLong', {max: 20})),
    });
